import React from "react";
import { motion } from "framer-motion";

/**
 * Reusable editorial section header: eyebrow + serif title + gold rule + lead.
 *
 * props:
 *  - eyebrow   small uppercase overline (optional)
 *  - title     serif heading (required)
 *  - lead      supporting paragraph (optional)
 *  - align     "center" (default) | "left"
 *  - tone      "light" (default, on cream) | "dark" (on ink)
 *  - className extra classes on the wrapper
 */
const SectionIntro = ({
  eyebrow,
  title,
  lead,
  align = "center",
  tone = "light",
  className = "",
}) => {
  const isCenter = align === "center";
  const isDark = tone === "dark";

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`${isCenter ? "text-center mx-auto" : "text-left"} max-w-3xl ${className}`}
    >
      {eyebrow && (
        <span
          className={`eyebrow block mb-5 ${
            isDark ? "text-brass-light" : "text-brass"
          }`}
        >
          {eyebrow}
        </span>
      )}

      <h2
        className={`font-serif font-light tracking-wide leading-[1.1] text-4xl md:text-5xl lg:text-[3.4rem] ${
          isDark ? "text-cream" : "text-ink"
        }`}
      >
        {title}
      </h2>

      <motion.div
        className={`rule-gold mt-7 ${isCenter ? "mx-auto" : ""}`}
        initial={{ width: 0 }}
        whileInView={{ width: 64 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
      />

      {lead && (
        <p
          className={`mt-7 text-base md:text-lg font-light leading-relaxed ${
            isCenter ? "mx-auto" : ""
          } max-w-2xl ${isDark ? "text-cream/70" : "text-ink-soft"}`}
        >
          {lead}
        </p>
      )}
    </motion.div>
  );
};

export default SectionIntro;
